import { useEffect, useRef, useState } from 'preact/hooks';
import type { ToolActivityMessage, ContextFullness } from '@voice-assistant/shared';
import type { ChatMessage } from '../hooks/useSocket';
import { renderAnsi, renderMarkdown } from '../utils/renderMarkdown';

interface TaggedToolActivity extends ToolActivityMessage {
  turnIndex: number;
}

interface Props {
  messages: ChatMessage[];
  toolActivities: TaggedToolActivity[];
  sessionMeta?: { numTurns?: number; totalCostUsd?: number };
  /** Server-computed occupancy union (context-fullness Phase 0b) for the active
   *  session — the status bar renders it and never derives a pct itself (SE-09). */
  fullness?: ContextFullness | null;
}

/** Format a tool activity line for console display */
function formatTool(ta: TaggedToolActivity): string {
  const input = ta.input || '';
  if (ta.type === 'end' && input) {
    // Show enriched end event with extracted input
    if (ta.tool === 'Bash') return `$ ${input}`;
    return `> ${ta.tool} ${input}`;
  }
  if (ta.type === 'start') {
    return `[${ta.tool}]`;
  }
  return `[${ta.tool}] ${input}`;
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

function pctColor(pct: number): string {
  if (pct < 40) return '#44cc88';
  if (pct < 70) return '#ccaa33';
  if (pct < 85) return '#cc8833';
  return '#cc4444';
}

function renderBar(pct: number): string {
  const filled = Math.round(pct / 5); // 20 chars wide
  const empty = 20 - filled;
  return '\u2588'.repeat(filled) + '\u2591'.repeat(empty);
}

export function ConsoleView({ messages, toolActivities, sessionMeta, fullness }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [statusExpanded, setStatusExpanded] = useState(false);
  /** Track whether user has scrolled away from bottom */
  const userScrolledRef = useRef(false);

  // Detect manual scroll: if user scrolls up, stop auto-scrolling
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userScrolledRef.current = distFromBottom > 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll only if user is near the bottom
  useEffect(() => {
    if (!userScrolledRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, toolActivities]);

  // Build interleaved entries using turnIndex for correct tool placement
  const entries: Array<{ type: 'user' | 'assistant' | 'error' | 'tool' | 'system'; text: string }> = [];

  let turnNum = 0;
  for (const msg of messages) {
    if (msg.role === 'system') {
      entries.push({ type: 'system', text: msg.text });
    } else if (msg.role === 'user') {
      turnNum++;
      entries.push({ type: 'user', text: msg.text });
      // Insert tool activities for this turn (only 'end' events with input, or 'start' if no end yet)
      const turnTools = toolActivities.filter((ta) => ta.turnIndex === turnNum);
      // Prefer 'end' events (they have enriched input), fall back to 'start'
      const seen = new Set<string>();
      for (const ta of turnTools) {
        if (ta.type === 'end') {
          seen.add(`${ta.tool}-${ta.input}`);
          entries.push({ type: 'tool', text: formatTool(ta) });
        }
      }
      // Add start events only for tools that never got an end event
      for (const ta of turnTools) {
        if (ta.type === 'start' && !turnTools.some((t) => t.type === 'end' && t.tool === ta.tool)) {
          entries.push({ type: 'tool', text: formatTool(ta) });
        }
      }
    } else if (msg.role === 'assistant') {
      entries.push({ type: 'assistant', text: msg.text });
    } else {
      entries.push({ type: 'error', text: msg.text });
    }
  }

  // Show tools for the current (still-streaming) turn that hasn't got an assistant response yet
  const streamingTools = toolActivities.filter((ta) => ta.turnIndex > turnNum);
  for (const ta of streamingTools) {
    if (ta.type === 'end') {
      entries.push({ type: 'tool', text: formatTool(ta) });
    } else if (ta.type === 'start' && !streamingTools.some((t) => t.type === 'end' && t.tool === ta.tool)) {
      entries.push({ type: 'tool', text: formatTool(ta) });
    }
  }

  const reading = fullness && (fullness.kind === 'measured' || fullness.kind === 'projected') ? fullness : null;
  const hasContext = !!reading || fullness?.kind === 'unknown-window';

  return (
    <div class="console-view" ref={containerRef}>
      {hasContext && (
        <div class="console-status-bar-wrap">
          <div
            class="console-status-bar"
            style={{ color: reading ? pctColor(reading.pct) : undefined, cursor: 'pointer' }}
            onClick={() => setStatusExpanded(!statusExpanded)}
          >
            {reading
              ? `[${renderBar(reading.pct)}] ${reading.kind === 'projected' ? '~' : ''}${reading.pct}% ${formatTokens(reading.contextTokens)}/${formatTokens(reading.contextWindow)}`
              : fullness?.kind === 'unknown-window'
                ? `${formatTokens(fullness.contextTokens)} / ?`
                : ''}
            {sessionMeta?.totalCostUsd != null && ` · $${sessionMeta.totalCostUsd.toFixed(3)}`}
            {sessionMeta?.numTurns != null && ` · ${sessionMeta.numTurns} msgs`}
          </div>
          {statusExpanded && (
            <div class="console-status-detail">
              <div class="console-status-row">
                <span>Context</span>
                <span style={{ color: reading ? pctColor(reading.pct) : undefined }}>
                  {reading
                    ? `${reading.kind === 'projected' ? '~' : ''}${reading.pct}%`
                    : fullness?.kind === 'unknown-window' ? `${formatTokens(fullness.contextTokens)} / ?` : '—'}
                </span>
              </div>
              {reading && (
                <div class="console-status-row">
                  <span>Tokens</span><span>{reading.kind === 'projected' ? '~' : ''}{formatTokens(reading.contextTokens)} / {formatTokens(reading.contextWindow)}</span>
                </div>
              )}
              {sessionMeta?.totalCostUsd != null && (
                <div class="console-status-row">
                  <span>Cost</span><span>${sessionMeta.totalCostUsd.toFixed(3)}</span>
                </div>
              )}
              {sessionMeta?.numTurns != null && (
                <div class="console-status-row">
                  <span>Messages</span><span>{sessionMeta.numTurns}</span>
                </div>
              )}
              {reading?.kind === 'measured' && reading.pct > 70 && (
                <div class="console-status-alert">Compaction approaching — older messages will be summarized</div>
              )}
            </div>
          )}
        </div>
      )}
      {entries.map((entry, i) => {
        if (entry.type === 'system') {
          return (
            <div key={i} class="session-divider">
              <span class="session-divider-line" />
              <span class="session-divider-text">{entry.text}</span>
              <span class="session-divider-line" />
            </div>
          );
        }
        if (entry.type === 'user') {
          return <div key={i} class="console-user">$ {entry.text}</div>;
        }
        if (entry.type === 'tool') {
          return <div key={i} class="console-tool" dangerouslySetInnerHTML={{ __html: renderAnsi(entry.text) }} />;
        }
        if (entry.type === 'error') {
          return <div key={i} class="console-error">{entry.text}</div>;
        }
        // Assistant: render markdown (headers, tables, code blocks, lists) with monospace terminal aesthetic
        return <div key={i} class="console-assistant" dangerouslySetInnerHTML={{ __html: renderMarkdown(entry.text) }} />;
      })}
      <div ref={bottomRef} />
    </div>
  );
}
